import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, NotebookPen, Mic, HeartPulse, GraduationCap, Users, Search } from 'lucide-react';

const FEATURES = [
  {
    id: 'daily-check-ins',
    icon: HeartPulse,
    title: 'Daily emotional check-ins',
    what: 'A 30-second ritual to name how you really feel. Log your mood, stress, sleep, and energy, add a short note, and watch patterns surface over time instead of staying bottled up.',
    who: 'For any man who moves fast and rarely stops to ask how he is actually doing — and wants an honest read on his week.',
  },
  {
    id: 'journaling',
    icon: NotebookPen,
    title: 'Guided & voice journaling',
    what: 'Private journaling with prompts that meet you where you are. Type it out, or tap the mic and speak — voice journaling turns what you say into a saved entry, so reflection fits a real schedule.',
    who: 'For men who think best out loud, or who never knew where to start with a blank page.',
    extraIcon: Mic,
  },
  {
    id: 'ai-support',
    icon: Sparkles,
    title: 'AI-powered wellness support',
    what: 'A supportive AI coach in your corner, any hour, to help you think through stress, relationships, and goals. It listens first, reflects what it hears, and helps you make a plan.',
    who: 'For the moments between — late nights, hard conversations, the stuff you are still figuring out.',
    disclaimer: 'The AI coach offers wellness support and reflection. It is not a licensed therapist, not a medical or mental-health professional, and not a substitute for professional diagnosis or treatment. In a crisis, use the crisis resources below.',
  },
  {
    id: 'learning',
    icon: GraduationCap,
    title: 'Learning resources',
    what: 'Short, practical lessons and growth plans on the things that actually move the needle — stress, anger, confidence, relationships, and showing up for the people who count on you.',
    who: 'For men who would rather build a skill than scroll another feed.',
  },
  {
    id: 'directory',
    icon: Search,
    title: 'Therapist directory',
    what: 'Browse a free, searchable directory of culturally responsive, licensed therapists. Filter by state, specialty, insurance, and language — no account required.',
    who: 'For when you are ready to talk to a professional and want someone who gets your context.',
    to: '/directory',
    cta: 'Browse the directory',
  },
  {
    id: 'community',
    icon: Users,
    title: 'A brotherhood that gets it',
    what: 'A members-only community to connect with other men doing the same inner work — honest, moderated, and private. You are not meant to carry it alone.',
    who: 'For every man who has felt like the only one in the room holding it together.',
  },
];

export default function Features() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="features-page">
      <div className="max-w-4xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="features-back">
            <ArrowLeft size={14}/> Home
          </Link>
          <Link to="/login?mode=signin" className="inline-flex items-center rounded-full border border-navy/15 text-navy text-sm font-semibold px-5 py-2 hover:bg-navy hover:text-white transition-smooth" data-testid="features-signin">
            Sign in
          </Link>
        </div>

        <header className="mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">Features</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-2 leading-tight text-navy">
            Everything in Brotherhood+, built for the inner work
          </h1>
          <p className="mt-4 text-lg text-navy/70 max-w-2xl leading-relaxed">
            Brotherhood+ is a men's emotional wellness app — created with men of color in mind, and open to every
            man ready to grow. Daily check-ins, journaling, a coach in your corner, real lessons, and a directory of
            culturally responsive therapists, in one private place.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register" className="inline-flex items-center justify-center rounded-full bg-navy text-white px-6 py-3 text-sm font-semibold hover:opacity-90 transition-smooth" data-testid="features-cta-primary">
              Start your 7-day free trial
            </Link>
            <Link to="/faq" className="inline-flex items-center justify-center rounded-full border border-navy/20 text-navy px-6 py-3 text-sm font-semibold hover:bg-navy/5 transition-smooth" data-testid="features-cta-faq">
              Read the FAQ
            </Link>
          </div>
        </header>

        <div className="space-y-5">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <section key={f.id} id={f.id} className="scroll-mt-20 rounded-3xl bg-white p-6 lg:p-8 card-soft" data-testid={`feature-${f.id}`}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-navy/5 text-gold">
                    <Icon size={22} aria-hidden="true"/>
                  </span>
                  <h2 className="font-heading text-2xl text-navy">{f.title}</h2>
                </div>
                <p className="mt-4 leading-relaxed text-navy/80">{f.what}</p>
                <p className="mt-2 text-sm text-navy/60"><span className="font-semibold text-navy/75">Who it helps:</span> {f.who}</p>
                {f.disclaimer && (
                  <p className="mt-4 rounded-2xl bg-[#F4F1EA] p-4 text-sm text-navy/70 leading-relaxed" data-testid={`feature-${f.id}-disclaimer`}>
                    {f.disclaimer}
                  </p>
                )}
                <div className="mt-5">
                  <Link to={f.to || '/register'} className="inline-flex items-center gap-1 text-sm font-semibold text-navy underline underline-offset-4 hover:text-gold transition-smooth" data-testid={`feature-${f.id}-cta`}>
                    {f.cta || 'Get started'} →
                  </Link>
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-12 rounded-3xl bg-navy text-white p-8 lg:p-10 text-center">
          <h2 className="font-heading text-3xl">Ready to do the work?</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto leading-relaxed">
            Create your account and start a 7-day free trial. After your trial, keep your membership for
            $14.99/month or $99.99/year (two months free). Cancel anytime.
          </p>
          <Link to="/register" className="mt-6 inline-flex items-center justify-center rounded-full bg-gold text-navy px-7 py-3 text-sm font-bold hover:opacity-90 transition-smooth" data-testid="features-cta-bottom">
            Create your account
          </Link>
          <p className="mt-5 text-xs text-white/45">
            In crisis? <Link to="/crisis" className="underline hover:text-white">See immediate crisis resources</Link>.
          </p>
        </div>

        <footer className="mt-12 pt-6 border-t border-navy/10 text-sm text-navy/55">
          <p>Brotherhood+ · Rooted in culture. Made for every man ready to grow.</p>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/about" className="underline hover:text-navy">About</Link>
            <Link to="/faq" className="underline hover:text-navy">FAQ</Link>
            <Link to="/directory" className="underline hover:text-navy">Therapist directory</Link>
            <Link to="/privacy" className="underline hover:text-navy">Privacy</Link>
            <Link to="/terms" className="underline hover:text-navy">Terms</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
